<template>
  <div>
    <div class="row page-title-header">
      <div class="col-12">
        <BreadCrumb
          parentRouteName="BookingSelectList" 
        />
      </div>
    </div>

    <b-card>
      <div class="d-flex mb-4 mb-xl-2 justify-content-between align-items-center">
        <h4 class="mb-0 font-weight-bold">預約管理</h4>
        <div class="d-flex align-items-center">
          <b-form-select
            v-model="query.is_valid"
            :options="validityStatusOptions"
            class="mr-3"
            @change="handlePageReset"
          />
          <b-form-select
            v-model="query.status"
            :options="publishStatusOptions"
            class="mr-3"
            @change="handlePageReset"
          />
          <b-input-group style="min-width: 300px;">
            <b-form-input
              v-model="searchKeyword"
              placeholder="搜尋"
              @keydown.enter="handleSearchKeyPress"
              @compositionstart="isComposing = true"
              @compositionend="isComposing = false"
            ></b-form-input>
            <b-input-group-append>
              <b-button @click="handleSearch">
                <i class="fa fa-search"></i>
              </b-button>
            </b-input-group-append>
          </b-input-group>
        </div>
      </div>
      <b-card-text>
        <div class="row">
          <div class="col-12">
            <b-overlay :show="showLoading" rounded="sm">
              <b-table
                striped
                hover
                responsive
                :items="bookingPresets"
                :fields="fields"
              >
                <template #cell(is_valid)="data">
                  <b-badge :variant="data.item.is_valid ? 'success' : 'danger'">
                    {{ data.item.is_valid ? '生效中' : '已失效' }}
                  </b-badge>
                </template>
                <template #cell(status)="data">
                  <b-badge
                    v-if="data.item.status == 'published'"
                    variant="success"
                  >
                    發布
                  </b-badge>
                  <b-badge v-if="data.item.status == 'closed'" variant="danger">
                    關閉
                  </b-badge>
                  <b-badge
                    v-if="data.item.status == 'draft'"
                    variant="warning"
                  >
                    草稿
                  </b-badge>
                </template>
                <template #cell(name)="data">
                  <RouterLink
                    :to="{ name: 'BookingList', params: { id: data.item.id } }"
                  >
                    {{ data.item.name }}
                  </RouterLink>
                </template>
                <template #cell(actions)="data">
                  <b-button
                    class="mr-1"
                    v-if="checkPermission([consts.BOOKING_LIST])"
                    variant="inverse-dark"
                    @click="showFormUrl(data.item)"
                  >
                    <i class="fa fa-link"></i>
                  </b-button>
                  <b-button
                    class="mr-1"
                    v-if="checkPermission([consts.BOOKING_LIST])"
                    variant="inverse-warning"
                    :to="{ name: 'BookingList', params: { id: data.item.id } }"
                  >
                    <span class="mdi mdi-format-list-bulleted"></span>
                    預約紀錄
                  </b-button>
                  <b-button
                    class="mr-1"
                    v-if="checkPermission([consts.BOOKING_BRANCH_SETTING_MODIFY])"
                    variant="inverse-dark"
                    :to="{
                      name: 'BookingBranchPresetBasicEdit',
                      params: {
                        id: data.item.id,
                        branch: branchId
                      }
                    }"
                  >
                    <i class="mdi mdi-settings"></i>
                    基本設定
                  </b-button>
                  <b-button
                    class="mr-1"
                    v-if="checkPermission([consts.BOOKING_SETTING]) &&
                          data.item.config &&
                          data.item.config.booking_mapping &&
                          data.item.config.booking_mapping.bookings &&
                          data.item.config.booking_mapping.bookings.start_at_by_branch !== null
                         "
                    variant="inverse-info"
                    :to="{
                      name: 'BookingBranchPresetEdit',
                      params: {
                        id: data.item.id,
                        branch: branchId
                      }
                    }"
                  >
                    <i class="mdi mdi-timetable"></i>
                    時段設定
                  </b-button>
                  <b-button
                    class="mr-1 btn-inverse-success"
                    v-if="checkPermission([consts.BOOKING_BRANCH_SHIFT_LIST_VIEW])"
                    :to="{ name: 'BookingBranchShiftList', params: { id: data.item.id } }"
                  >
                    <i class="mdi mdi-sleep"></i>
                    店休設定
                  </b-button>
                </template>
              </b-table>
            </b-overlay>
          </div>
        </div>
        <CustomPagination
          :currentPage="query.page"
          :totalRows="totalRows"
          :perPage="query.per_page"
          @page-change="handlePageChange"
          @per-page-change="handlePerPageChange"
        />
      </b-card-text>
    </b-card>

    <FormUrlModal ref="formUrlModal" />
  </div>
</template>

<script>
import * as consts from "@/consts";
import PermissionChecker from "@/utils/PermissionChecker";
import { format } from "date-fns";
import bookingApi from "@/apis/booking";
import { mapState, mapGetters } from "vuex";
import CustomPagination from "@/components/Page/Dashboard/CustomPagination.vue";
import FormUrlModal from "@/components/Dashboard/Booking/FormUrlModal.vue";
import { paginationMixin } from "@/mixins/pagination";
import { updateQueryFromRoute, updateUrl } from "@/utils/updateUrl";
import BreadCrumb from "@/components/Dashboard/BreadCrumb";

export default {
  components: {
    CustomPagination,
    FormUrlModal,
    BreadCrumb
  },
  mixins: [paginationMixin],
  data() {
    return {
      consts: consts,
      showLoading: false,
      fieldsInit: false,
      branchId: null,
      searchKeyword: "",
      isComposing: false,
      validityStatusOptions: [
        { value: null, text: "選擇生效狀態" },
        { value: true, text: "生效中" },
        { value: false, text: "已失效" },
      ],
      publishStatusOptions: [
        { value: null, text: "選擇發布狀態" },
        { value: "published", text: "發布" },
        { value: "draft", text: "草稿" },
        { value: "closed", text: "關閉" },
      ],
      fields: [
        {
          key: "name",
          label: "預約名稱",
          sortable: true,
        },
        { key: "is_valid", label: "生效狀態", sortable: false },
        { key: "status", label: "發布狀態", sortable: false },
        { key: "bookings_count", label: "目前預約筆數", sortable: true },
        {
          key: "created_at",
          label: "建立時間",
          sortable: true,
          formatter: (value) => {
            return format(new Date(value), "yyyy-MM-dd HH:mm:ss");
          },
        },
        {
          key: "actions",
          label: "管理",
        },
      ],
      bookingPresets: [],
      totalRows: 0,
      query: {
        keyword: "",
        page: 1,
        per_page: 20,
        is_valid: null,
        status: null,
        sort_by: "created_at",
        order_by: "desc",
      },
    };
  },
  computed: {
    ...mapState("general", {
      organization: (state) => state.organization,
    }),
    ...mapGetters("general", ["currentBranch"]),
    queryWatcher() {
      return `${this.query.page}-${this.query.per_page}-${this.query.keyword}-${this.query.is_valid}-${this.query.status}-${this.query.sort_by}-${this.query.order_by}`;
    },
  },
  watch: {
    queryWatcher: {
      handler() {
        if (!this.initialized) return;
        updateUrl(this.query, this.$store, this.$router);
      },
    },
    $route(to) {
      if (!this.initialized) return;
      this.query = updateQueryFromRoute(to, this.$store, this.query);
      this.searchKeyword = this.query.keyword || "";
      this.fetchBookingPresets();
    },
  },
  created() {
    this.query = updateQueryFromRoute(this.$route, this.$store, this.query);
    this.searchKeyword = this.query.keyword || "";
    if (this.currentBranch) {
      this.branchId = this.currentBranch.id
    }
    if (this.$route.query.branch_id) {
      this.branchId = this.$route.query.branch_id
    }
  },
  async mounted() {
    await this.fetchBookingPresets();
    this.initialized = true;
  },
  methods: {
    checkPermission(permissions) {
      const checker = new PermissionChecker();
      return checker.check(permissions);
    },
    async fetchBookingPresets() {
      try {
        this.showLoading = true;
        const params = { ...this.query };
        if (params.is_valid === null) {
          delete params.is_valid;
        }
        if (params.status === null) {
          delete params.status;
        }
        const response = await bookingApi.getBookingPresets(params);
        this.bookingPresets = response.data.data;
        this.totalRows = response.data.meta.total;
      } catch (error) {
        console.error(error);
        this.$swal("錯誤", "讀取錯誤", "error");
      } finally {
        this.showLoading = false;
      }
    },
    showFormUrl(bookingPreset) {
      this.$refs.formUrlModal.showFormUrl(bookingPreset);
    },
    handleSearchKeyPress(event) {
      if (this.isComposing) {
        event.preventDefault();
      } else {
        this.handleSearch();
      }
    },
    handleSearch() {
      this.query.keyword = this.searchKeyword;
      this.handlePageReset();
    },
  },
};
</script>
